import { Node, TileNode } from '@/components/diagrams/FlowChart';
import {
  applyDeltaToNodes,
  computeCentroid,
} from '@/components/diagrams/FlowChart/utils/centroid';
import { gridLayout } from '@/components/diagrams/FlowChart/utils/gridLayout';
import { isNodeEligibleForGraveyard } from '@/modules/graphViz/graphVizNodeConfig/nodeGraveyard.utils';

import {
  ENTITY_DIAGRAM_GRID_CHUNK_SIZE,
  X_SPACING,
  Y_SPACING,
} from '../EntityDiagram.constants';
import { EntityDiagramState, EntityDiagramVariant } from '../types';
import { applyDerivedDataToState } from './applyDerivedDataToState';

function hasNonZeroPosition({ position }: Node): boolean {
  return !!(position.x || position.y);
}

/**
 * @description A view config is "fully initialized" as a UI only concept when
 * there are nodes with positions that contain non-zero values
 */
export function hasFullyInitializedNodes(state: EntityDiagramState) {
  const { derivedData } = applyDerivedDataToState(state);

  // We only check tile nodes, section group position is managed by a different process
  const nodes = derivedData.nodesByType.tile;
  return nodes?.length && nodes.some(hasNonZeroPosition);
}

/**
 * @description Sets the `isNewTile` flag to false for all nodes in the graveyard
 */
function removeNodeFromGraveyard(node: Node): Node {
  if (isNodeEligibleForGraveyard(node)) {
    // We want to remove all nodes from the graveyard
    return { ...node, data: { ...node.data, isNewTile: false } } as TileNode;
  }
  return node;
}

export function applyAutoLayoutToState(state: EntityDiagramState): {
  state: EntityDiagramState;
  grid: TileNode[][];
} {
  const {
    derivedData: { nodesByType },
  } = applyDerivedDataToState(state);

  // Section group layout managed by a different process
  const allTileNodes = (nodesByType.tile ?? []) as TileNode[];
  const groupedMemberNodes = allTileNodes.filter(
    (node) => node.data.hiddenByVisualizationGroup
  );
  const tileNodes = allTileNodes.filter(
    (node) => !node.data.hiddenByVisualizationGroup
  );

  const sectionLabelNodes = nodesByType.sectionLabel ?? [];
  const textNoteNodes = nodesByType.textNote ?? [];

  // Compute centroid before layout so we can shift text notes by the same delta
  const preCentroid = computeCentroid(tileNodes);

  const tileNodesZeroed = tileNodes.map((node) => ({
    ...node,
    position: { x: 0, y: 0 },
  }));

  // Apply the grid layout to the tile nodes
  const { grid, edges } = gridLayout({
    nodes: tileNodesZeroed,
    chunkSize: ENTITY_DIAGRAM_GRID_CHUNK_SIZE,
    paddingX: X_SPACING,
    // Give more padding for ownership since we do not show
    // the section labels
    paddingY:
      state.entityDiagramVariant === EntityDiagramVariant.Ownership
        ? 100
        : Y_SPACING,
    edges: state.edges,
  });

  const nodes = grid.flat().map(removeNodeFromGraveyard);

  // Shift text notes by the same amount the diagram moved
  const postCentroid = computeCentroid(nodes);
  const delta = {
    x: postCentroid.x - preCentroid.x,
    y: postCentroid.y - preCentroid.y,
  };
  const shiftedTextNoteNodes = applyDeltaToNodes(textNoteNodes, delta);

  return {
    state: {
      ...state,
      nodes: [
        ...sectionLabelNodes,
        ...nodes,
        ...groupedMemberNodes,
        ...shiftedTextNoteNodes,
      ],
      edges,
    },
    grid,
  };
}
